import { Controller } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';
import TitleField from '../fields/TitleField';
import SourceField from '../fields/SourceField';
import { useMemoFormContext } from '../context/FormContext';
import { useEffect } from 'react';
import { MemoType } from 'models/Memo';
import { requireContentForType } from '../utils/validators';
import { useTranslations } from 'next-intl';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';

const TextForm: React.FC = () => {
  const {
    control,
    isTitleVisible,
    isSourceVisible,
    isFullyExpanded,
    getValues,
  } = useMemoFormContext();

  const { trigger } = useMemoFormContext();
  const t = useTranslations();
  const { selectedBox } = useActiveSpace();

  // Same wording as the Mac composer: name the box the memo will land in.
  const placeholder = selectedBox?.name
    ? t('memoComposer.placeholder', { box: selectedBox.name })
    : t('memoComposer.placeholderNoBox');

  // NOTE: Validation is triggered here in TextForm instead of in `useFormInitialisation`
  // because fields only register after their component mounts. Attempting to trigger validation
  // too early (e.g., during `useFormInitialisation` or on the next tick) fails as the `content`
  // field doesn't yet exist. Triggering validation directly where the field is rendered ensures
  // the field is registered and can be validated immediately.
  useEffect(() => {
    trigger();
  }, [trigger]);

  return (
    <div>
      {isTitleVisible && <TitleField />}

      <Controller
        control={control}
        name="content"
        render={({ field: { onChange, value, onBlur } }) => (
          <TextareaAutosize
            id={'content'}
            minRows={1}
            maxRows={isFullyExpanded === false ? 6 : undefined}
            name={'content'}
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            className="block w-full bg-transparent border-0 py-0 px-0 text-sm resize-none placeholder-ink-tertiary focus:ring-0"
            placeholder={placeholder}
          />
        )}
        rules={{
          validate: requireContentForType(
            getValues,
            MemoType.Text,
            t('memoComposer.contentRequired'),
          ),
        }}
      />

      {isSourceVisible && <SourceField />}
    </div>
  );
};

export default TextForm;
